import { Stack, StackProps, styled, Theme } from '@mui/material';
import { FC } from 'react';

export type TagSimpleVariant = 'filled' | 'outlined';
const TAG_SIMPLE_COLORS = ['default', 'attention', 'error', 'success'] as const;

export type TagSimpleColor = (typeof TAG_SIMPLE_COLORS)[number];

export type TagSimpleProps = Omit<StackProps, 'color'> & {
  variant?: TagSimpleVariant;
  color?: TagSimpleColor;
};

const TEXT_VAR = '--tag-simple-text';
const BACKGROUND_VAR = '--tag-simple-background';
const BORDER_VAR = '--tag-simple-border';

type Tokens = {
  text: string;
  background: string | undefined;
  border: string;
};

const getTokens = (theme: Theme, color: TagSimpleColor): Tokens => {
  switch (color) {
    case 'default':
      return {
        text: theme.palette.common.grey2,
        background: theme.palette.common.lightBg,
        border: theme.palette.divider,
      };
    case 'attention':
      return {
        text: theme.palette.common.orange,
        background: theme.palette.common.orange5,
        border: theme.palette.common.orange,
      };
    case 'error':
      return {
        text: theme.palette.error.main,
        background: theme.palette.error.superLight,
        border: theme.palette.error.main,
      };
    case 'success':
      return {
        text: theme.palette.success.main,
        background: theme.palette.success.superLight,
        border: theme.palette.success.main,
      };
  }
};

// MUI v6 migration: `variants[]` generated from a token map, matching the
// MUI Chip/Button idiom. CSS vars keep `sx` overrides working.
const TagRoot = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'color',
})<{ variant: TagSimpleVariant; color: TagSimpleColor }>(({ theme }) => ({
  alignItems: 'center',
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(0, 1),
  color: `var(${TEXT_VAR})`,
  backgroundColor: `var(${BACKGROUND_VAR})`,
  border: `1px solid var(${BORDER_VAR})`,
  variants: TAG_SIMPLE_COLORS.flatMap((color) => {
    const tokens = getTokens(theme, color);
    return [
      { props: { color }, style: { [TEXT_VAR]: tokens.text } },
      {
        props: { variant: 'filled' as const, color },
        style: { [BACKGROUND_VAR]: tokens.background, [BORDER_VAR]: 'transparent' },
      },
      {
        props: { variant: 'outlined' as const, color },
        style: { [BACKGROUND_VAR]: 'transparent', [BORDER_VAR]: tokens.border },
      },
    ];
  }),
}));

export const TagSimple: FC<TagSimpleProps> = ({
  variant = 'filled',
  color = 'default',
  children,
  ...props
}) => {
  return (
    <TagRoot variant={variant} color={color} {...props}>
      {children}
    </TagRoot>
  );
};
